'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  assistantActionKey,
  useAssistantActionsStore,
} from '@/shared/assistant';
import { useUpscaleProgress } from '@/features/design/album/hooks/use-upscale-progress';
import { useSwitchAppLocale } from '@/features/i18n';
import { createLogger } from '@/shared/lib/logger';
import type { MiddlePanelTab } from '@/features/project/context/DesktopLayoutContext';
import type {
  AssistantAction,
  AssistantActionCardBlock,
} from '@/types/assistant';
import { designPath } from '../lib/routes';

const logger = createLogger('Assistant');

/** How long a card waits for its screen to mount after navigating there. */
const MOUNT_WAIT_MS = 10_000;

/** The design tab that owns each action's regular button. */
const ACTION_TAB: Record<
  Exclude<AssistantAction, 'upscale_render' | 'switch_language'>,
  MiddlePanelTab
> = {
  start_render: 'design',
  order_estimate: 'estimate',
  regenerate_estimate: 'estimate',
  order_album: 'album',
};

/**
 * The primary button of an `action_card` (ADR-001 §5): it calls the SAME
 * function the regular button in the interface calls, so price checks,
 * preflight paywalls, the API's refusal for insufficient funds and the job
 * tracking all stay in one place. The screen owning that button publishes it
 * through `useAssistantAction`; when it is not on screen, the card opens it
 * and presses the button once it has mounted.
 */
export function useAssistantActionRunner(fallbackDesignId: string | null) {
  const router = useRouter();
  // Upscale has no screen of its own: its regular button (the render menu)
  // calls this hook's function directly, and so does the card.
  const { upscaleRender } = useUpscaleProgress();
  const { switchLocale } = useSwitchAppLocale();

  return useCallback(
    (card: AssistantActionCardBlock) => {
      const params = card.params ?? {};
      const action = card.action as AssistantAction;

      if (action === 'upscale_render') {
        if (params.render_id) void upscaleRender(params.render_id);
        return;
      }
      // The server's one-time offer to show the app in the conversation's
      // language: the same switch as the language selector.
      if (action === 'switch_language') {
        if (params.locale) void switchLocale(params.locale);
        return;
      }
      if (!(action in ACTION_TAB)) {
        logger.info('Ignoring unknown assistant action', { action });
        return;
      }

      const designId = params.design_id ?? fallbackDesignId;
      const scopeId =
        action === 'regenerate_estimate' ? params.estimate_id : designId;
      if (!scopeId) return;

      const key = assistantActionKey(action, scopeId);
      const handler = useAssistantActionsStore.getState().handlers[key];
      if (handler) {
        void handler();
        return;
      }

      if (!params.building_id || !designId) {
        logger.warn('Action card without a screen to open', { action });
        return;
      }
      router.push(designPath(params.building_id, designId, ACTION_TAB[action]));

      const timer = window.setTimeout(() => {
        unsubscribe();
        logger.info('Action screen did not mount in time', { action });
      }, MOUNT_WAIT_MS);
      const unsubscribe = useAssistantActionsStore.subscribe((state) => {
        const mounted = state.handlers[key];
        if (!mounted) return;
        unsubscribe();
        window.clearTimeout(timer);
        void mounted();
      });
    },
    [fallbackDesignId, router, switchLocale, upscaleRender]
  );
}
